// Types and Data for Holland RIASEC Career Interests survey
// Inspired by John Holland's theory of career choice - original implementation for GROWNi.sk

import { v4 as uuidv4 } from 'uuid'
import type { Timestamp } from '../firebase.ts'
import type { UserId } from '../ids.ts'
import { SurveyTypes } from '../Survey.ts'

export type HollandSurveyId = string

export interface HollandSurvey {
    id: HollandSurveyId
    surveyType: SurveyTypes.HOLLAND
    userId: UserId
    hash?: string
    questions: HollandQuestionType[]
    scores: HollandScores
    dominantType: HollandType
    secondaryType?: HollandType
    hollandCode: string // e.g., "RIA" - top 3 types
    confidence: number // 0-100
    isDraft?: boolean
    createdAt: Timestamp
    updatedAt?: Timestamp
    deletedAt?: Timestamp | null
}

// Holland's 6 RIASEC types
export enum HollandType {
    REALISTIC = 'realistic', // R - Realistický
    INVESTIGATIVE = 'investigative', // I - Výskumný
    ARTISTIC = 'artistic', // A - Umelecký
    SOCIAL = 'social', // S - Sociálny
    ENTERPRISING = 'enterprising', // E - Podnikavý
    CONVENTIONAL = 'conventional' // C - Konvenčný
}

export const HollandTypeLabels: Record<HollandType, string> = {
    [HollandType.REALISTIC]: 'Realistický (R)',
    [HollandType.INVESTIGATIVE]: 'Výskumný (I)',
    [HollandType.ARTISTIC]: 'Umelecký (A)',
    [HollandType.SOCIAL]: 'Sociálny (S)',
    [HollandType.ENTERPRISING]: 'Podnikavý (E)',
    [HollandType.CONVENTIONAL]: 'Konvenčný (C)'
}

export const HollandTypeDescriptions: Record<HollandType, string> = {
    [HollandType.REALISTIC]:
        'Preferuješ prácu s nástrojmi, strojmi a vecami. Rád/a pracuješ rukami a riešiš praktické problémy.',
    [HollandType.INVESTIGATIVE]:
        'Baví ťa pozorovať, analyzovať a riešiť komplexné problémy. Rád/a sa učíš nové veci a skúmaš.',
    [HollandType.ARTISTIC]:
        'Máš tvorivú dušu a rád/a sa vyjadrúješ cez umenie, dizajn alebo písanie. Vyhľadávaš originalitu.',
    [HollandType.SOCIAL]: 'Preferuješ prácu s ľuďmi - pomáhať, učiť, radiť. Baví ťa spolupráca a rozvoj druhých.',
    [HollandType.ENTERPRISING]:
        'Rád/a vedieš, presvedčuješ a organizuješ. Baví ťa podnikanie, predaj a dosiahnutie cieľov.',
    [HollandType.CONVENTIONAL]:
        'Preferuješ organizovanú prácu s dátami a systémami. Rád/a pracuješ s detailmi a štruktúrou.'
}

export interface HollandQuestionType {
    id: string
    question: string // "Ktorá z týchto aktivít ťa baví najviac?"
    options: HollandOption[]
    value?: string // Selected option ID
}

export interface HollandOption {
    id: string
    text: string
    type: HollandType
}

export interface HollandScores {
    [HollandType.REALISTIC]: number
    [HollandType.INVESTIGATIVE]: number
    [HollandType.ARTISTIC]: number
    [HollandType.SOCIAL]: number
    [HollandType.ENTERPRISING]: number
    [HollandType.CONVENTIONAL]: number
}

// 72 forced-choice triads - each presenting 3 activities from different types
// Each type appears in 36 triads, ensuring balanced coverage
// We select 24 questions for each test (4 opportunities per type)
export const HOLLAND_QUESTIONS: HollandQuestionType[] = [
    // === Triad Set 1: R-A-S (Realistic, Artistic, Social) ===
    {
        id: uuidv4(),
        question: 'Ktorá z týchto aktivít ťa baví najviac?',
        options: [
            { id: uuidv4(), text: 'Opravovať bicykel alebo motorku', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Písať kreativne príbehy alebo básne', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Pomáhať priateľom s osobnými problémami', type: HollandType.SOCIAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'V ktorej situácii by si exceloval/a?',
        options: [
            { id: uuidv4(), text: 'Práca v dielni alebo na stavbe', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Tvorba umeleckého diela alebo performance', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Organizovanie charitnej akcie', type: HollandType.SOCIAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Ktorá aktivita ťa najviac oslovuje?',
        options: [
            { id: uuidv4(), text: 'Pestovať rastliny alebo chovať zvieratá', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Komponovať hudbu alebo tvoriť grafiku', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Trénovať šport alebo viesť workshopy', type: HollandType.SOCIAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Ktorú aktivitu by si preferoval/a?',
        options: [
            { id: uuidv4(), text: 'Pracovať vonku v prírode', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Maľovať obrazy alebo socháriť', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Viesť skupinové diskusie', type: HollandType.SOCIAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Ktorá úloha by ťa zaujala?',
        options: [
            { id: uuidv4(), text: 'Šoférovať alebo obsluhovať stroje', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Tvoriť hudbu alebo choreografiu', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Radiť ľuďom v ťažkých situáciách', type: HollandType.SOCIAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Ktorá úloha ťa priťahuje?',
        options: [
            { id: uuidv4(), text: 'Konštruovať prototypy alebo modely', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Navrhovať oblečenie alebo šperky', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Organizovať komunitné podujatia', type: HollandType.SOCIAL }
        ]
    },
    // New R-A-S questions
    {
        id: uuidv4(),
        question: 'Ako by si najradšej trávil/a pracovný deň?',
        options: [
            { id: uuidv4(), text: 'Montážou a údržbou zariadení', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Tvorbou vizuálneho obsahu a dizajnu', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Rozhovormi a pomocou klientom', type: HollandType.SOCIAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Čo by ťa motivovalo ísť ráno do práce?',
        options: [
            { id: uuidv4(), text: 'Vidieť konkrétne výsledky mojej práce', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Možnosť tvoriť niečo jedinečné', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Vedieť, že niekomu pomáham', type: HollandType.SOCIAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'V akom prostredí by si sa cítil/a najpríjemnejšie?',
        options: [
            { id: uuidv4(), text: 'V dielni s nástrojmi a materiálom', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'V štúdiu plnom farieb a inšpirácie', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'V centre plnom ľudí, ktorým môžem pomôcť', type: HollandType.SOCIAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Ktorý projekt by ťa najviac nadchol?',
        options: [
            { id: uuidv4(), text: 'Postaviť záhradný domček alebo nábytok', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Vytvoriť krátky film alebo podcast', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Zorganizovať mentorský program pre mladých', type: HollandType.SOCIAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Čo by si robil/a, keby si mal/a voľný mesiac?',
        options: [
            { id: uuidv4(), text: 'Renovoval/a by som niečo praktické', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Učil/a by som sa nové umelecké techniky', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Dobrovoľníčil/a by som v komunite', type: HollandType.SOCIAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Pri výbere práce by bolo pre teba najdôležitejšie:',
        options: [
            { id: uuidv4(), text: 'Pracovať s modernými technológiami a nástrojmi', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Mať slobodu v kreatívnom vyjadrení', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Cítiť, že moja práca má pozitívny dopad na ľudí', type: HollandType.SOCIAL }
        ]
    },

    // === Triad Set 2: I-E-C (Investigative, Enterprising, Conventional) ===
    {
        id: uuidv4(),
        question: 'V akej aktivite by si sa cítil/a najlepšie?',
        options: [
            { id: uuidv4(), text: 'Analyzovať dáta a hľadať vzorce', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Viesť tím a organizovať projekt', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Spravovať účtovníctvo a dokumenty', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Čo by ťa najviac napĺňalo?',
        options: [
            { id: uuidv4(), text: 'Riešiť vedecké alebo technické problémy', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Vyjednávať obchodné dohody', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Spracovávať a triediť informácie systematicky', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Čo by bolo pre teba ideálne?',
        options: [
            { id: uuidv4(), text: 'Robiť výskum a publikovať zistenia', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Presvedčovať ľudí o svojich nápadoch', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Pracovať s číslami a tabuľkami', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Čo by ti prinieslo najväčšie uspokojenie?',
        options: [
            { id: uuidv4(), text: 'Analyzovať chemické alebo biologické vzorky', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Začať vlastný biznis', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Spravovať rozpočet a financie', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Čo by ťa najviac motivovalo?',
        options: [
            { id: uuidv4(), text: 'Objavovať nové vedecké poznatky', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Viesť tím k úspechu', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Zabezpečiť presnosť a poriadok', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Čo by ti dávalo najväčší zmysel?',
        options: [
            { id: uuidv4(), text: 'Skúmať nové technológie', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Lobbovať za dôležité zmeny', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Kontrolovať kvalitu a dodržiavanie štandardov', type: HollandType.CONVENTIONAL }
        ]
    },
    // New I-E-C questions
    {
        id: uuidv4(),
        question: 'Aký typ problému by si riešil/a najradšej?',
        options: [
            { id: uuidv4(), text: 'Komplexný vedecký problém vyžadujúci analýzu', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Ako expandovať biznis na nové trhy', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Ako optimalizovať procesy a znížiť chyby', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Ktorá činnosť by ťa najviac bavila?',
        options: [
            { id: uuidv4(), text: 'Čítať odborné štúdie a články', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Prezentovať pred investormi', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Vytvárať detailné reporty a analýzy', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Čo by si považoval/a za úspech v práci?',
        options: [
            { id: uuidv4(), text: 'Objaviť niečo nové, čo nikto predtým nevedel', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Vybudovať úspešný tím alebo firmu', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Dosiahnuť dokonalú organizáciu a efektivitu', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'V akej situácii by si bol/a najproduktívnejší/najproduktívnejšia?',
        options: [
            { id: uuidv4(), text: 'Keď môžem v pokoji premýšľať a skúmať', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Keď môžem viesť a motivovať ostatných', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Keď mám jasné úlohy a postupy', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Ktorá vlastnosť by ťa najlepšie charakterizovala v práci?',
        options: [
            { id: uuidv4(), text: 'Zvedavosť a analytické myslenie', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Ambícia a schopnosť presvedčiť', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Precíznosť a spoľahlivosť', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Ako by si prispel/a k tímu?',
        options: [
            { id: uuidv4(), text: 'Hĺbkovou analýzou a odbornými znalosťami', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Vedením, motiváciou a strategickým myslením', type: HollandType.ENTERPRISING },
            {
                id: uuidv4(),
                text: 'Organizáciou, dokumentáciou a dodržiavaním termínov',
                type: HollandType.CONVENTIONAL
            }
        ]
    },

    // === Triad Set 3: R-I-A (Realistic, Investigative, Artistic) ===
    {
        id: uuidv4(),
        question: 'Čo by ťa bavilo robiť vo voľnom čase?',
        options: [
            { id: uuidv4(), text: 'Stavať alebo montovať veci', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Čítať vedecké články a experimentovať', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Navrhovať oblečenie alebo interiéry', type: HollandType.ARTISTIC }
        ]
    },
    {
        id: uuidv4(),
        question: 'Ktorú činnosť by si si vybral/a?',
        options: [
            { id: uuidv4(), text: 'Pracovať s nástrojmi a strojmi', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Skúmať prírodné javy v teréne', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Hrať v divadle alebo natáčať filmy', type: HollandType.ARTISTIC }
        ]
    },
    {
        id: uuidv4(),
        question: 'Ktorá práca by ťa najviac bavila?',
        options: [
            { id: uuidv4(), text: 'Opravovať elektroniku alebo počítače', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Programovať a riešiť algoritmy', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Fotografovať alebo strihať videá', type: HollandType.ARTISTIC }
        ]
    },
    {
        id: uuidv4(),
        question: 'V akej oblasti by si sa chcel/a rozvíjať?',
        options: [
            { id: uuidv4(), text: 'Práca s materiálmi a výrobou', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Matematika a logické myslenie', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Kreatívne písanie a storytelling', type: HollandType.ARTISTIC }
        ]
    },
    {
        id: uuidv4(),
        question: 'V ktorej činnosti by si vynikal/vynikala?',
        options: [
            { id: uuidv4(), text: 'Tesárske alebo zámočnícke práce', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Laboratórna práca a experimenty', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Návrhárstvo módy alebo interiérov', type: HollandType.ARTISTIC }
        ]
    },
    {
        id: uuidv4(),
        question: 'V ktorej role by si sa videl/videla?',
        options: [
            { id: uuidv4(), text: 'Stolár, mechanik alebo elektrikár', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Vedec, programátor alebo analytik', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Umelec, dizajnér alebo spisovateľ', type: HollandType.ARTISTIC }
        ]
    },
    // New R-I-A questions
    {
        id: uuidv4(),
        question: 'Aký druh úloh by ťa najviac napĺňal?',
        options: [
            { id: uuidv4(), text: 'Fyzická práca s viditeľnými výsledkami', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Intelektuálne výzvy a riešenie problémov', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Tvorba niečoho estetického a originálneho', type: HollandType.ARTISTIC }
        ]
    },
    {
        id: uuidv4(),
        question: 'Ktorý predmet v škole ťa bavil najviac?',
        options: [
            { id: uuidv4(), text: 'Praktické predmety a dielne', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Prírodovedné predmety a matematika', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Výtvarná výchova a literatúra', type: HollandType.ARTISTIC }
        ]
    },
    {
        id: uuidv4(),
        question: 'Ako riešiš problémy?',
        options: [
            { id: uuidv4(), text: 'Vyskúšam praktické riešenie a upravím podľa potreby', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Najprv dôkladne analyzujem a potom konám', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Hľadám nekonvenčné a kreatívne prístupy', type: HollandType.ARTISTIC }
        ]
    },
    {
        id: uuidv4(),
        question: 'V akom type firmy by si pracoval/a najradšej?',
        options: [
            { id: uuidv4(), text: 'Výrobná firma alebo remeselná dielňa', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Výskumná inštitúcia alebo laboratórium', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Kreatívna agentúra alebo ateliér', type: HollandType.ARTISTIC }
        ]
    },
    {
        id: uuidv4(),
        question: 'Čo by si robil/a, keby si vyhral/a v lotérii?',
        options: [
            { id: uuidv4(), text: 'Kúpil/a by som si dielňu a nástroje', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Financoval/a by som vlastný výskum', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Otvoril/a by som si galériu alebo štúdio', type: HollandType.ARTISTIC }
        ]
    },
    {
        id: uuidv4(),
        question: 'Ktorá forma vzdelávania by ti vyhovovala?',
        options: [
            { id: uuidv4(), text: 'Praktické kurzy s hands-on skúsenosťami', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Akademické štúdium s výskumom', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Umelecké workshopy a tvorivé laboratóriá', type: HollandType.ARTISTIC }
        ]
    },

    // === Triad Set 4: S-E-C (Social, Enterprising, Conventional) ===
    {
        id: uuidv4(),
        question: 'Ktorá práca ťa láka najviac?',
        options: [
            { id: uuidv4(), text: 'Doučovať deti alebo mentorovať mladých ľudí', type: HollandType.SOCIAL },
            { id: uuidv4(), text: 'Predávať produkty a získavať klientov', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Organizovať archív a spravovať databázu', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'V čom by si bol/a najlepší/najlepšia?',
        options: [
            { id: uuidv4(), text: 'Poradenstvo a podpora iných ľudí', type: HollandType.SOCIAL },
            { id: uuidv4(), text: 'Riadenie firmy alebo oddelenia', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Vedenie evidencie a kontrola kvality', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'V ktorom prostredí by si sa cítil/a najlepšie?',
        options: [
            { id: uuidv4(), text: 'Práca s deťmi alebo seniormi', type: HollandType.SOCIAL },
            { id: uuidv4(), text: 'Vedenie obchodných rokovaní', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Správa dokumentov a administratíva', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Ktorá činnosť ťa oslovuje najviac?',
        options: [
            { id: uuidv4(), text: 'Učiteľ, psychológ alebo zdravotník', type: HollandType.SOCIAL },
            { id: uuidv4(), text: 'Manažér, marketér alebo podnikateľ', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Účtovník, knihovník alebo administrátor', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Ktorá práca by ťa naplňovala?',
        options: [
            { id: uuidv4(), text: 'Pomáhať ľuďom riešiť konflikty', type: HollandType.SOCIAL },
            { id: uuidv4(), text: 'Predávať a získavať nových partnerov', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Organizovať a plánovať procesy', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Ktorá pozícia by ťa lákala?',
        options: [
            { id: uuidv4(), text: 'Sociálny pracovník alebo terapeut', type: HollandType.SOCIAL },
            { id: uuidv4(), text: 'Obchodný riaditeľ alebo konzultant', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Audítor alebo daňový poradca', type: HollandType.CONVENTIONAL }
        ]
    },
    // New S-E-C questions
    {
        id: uuidv4(),
        question: 'Aký typ interakcie s ľuďmi preferuješ?',
        options: [
            { id: uuidv4(), text: 'Hlboké, empatické rozhovory o osobných témach', type: HollandType.SOCIAL },
            { id: uuidv4(), text: 'Motivačné rozhovory zamerané na výsledky', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Štruktúrované stretnutia s jasnou agendou', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Čo by si robil/a ako dobrovoľník?',
        options: [
            { id: uuidv4(), text: 'Pomáhal/a ľuďom v núdzi priamo', type: HollandType.SOCIAL },
            { id: uuidv4(), text: 'Organizoval/a fundraisingové akcie', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Spravoval/a administratívu organizácie', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Aký typ zodpovednosti by ti vyhovoval?',
        options: [
            { id: uuidv4(), text: 'Zodpovednosť za pohodu a rozvoj ľudí', type: HollandType.SOCIAL },
            { id: uuidv4(), text: 'Zodpovednosť za rast a výsledky tímu', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Zodpovednosť za presnosť a dodržanie pravidiel', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Čo by ťa na práci motivovalo najviac?',
        options: [
            { id: uuidv4(), text: 'Vidieť, ako sa ľuďom darí vďaka mojej pomoci', type: HollandType.SOCIAL },
            { id: uuidv4(), text: 'Dosiahnuť obchodné ciele a uznanie', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Mať všetko pod kontrolou a v poriadku', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Ako by si riešil/a tímový konflikt?',
        options: [
            { id: uuidv4(), text: 'Vypočul/a by som obe strany a hľadal/a pochopenie', type: HollandType.SOCIAL },
            { id: uuidv4(), text: 'Rázne by som rozhodol/a a posunul/a tím ďalej', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Odkázal/a by som na pravidlá a postupy', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Aký typ úspechu by ťa najviac potešil?',
        options: [
            { id: uuidv4(), text: 'Keď niekto povie, že som mu zmenil/a život', type: HollandType.SOCIAL },
            { id: uuidv4(), text: 'Keď dosiahnem rekordné tržby alebo rast', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Keď projekt prebehne bez jedinej chyby', type: HollandType.CONVENTIONAL }
        ]
    },

    // === Triad Set 5: R-E-S (Realistic, Enterprising, Social) ===
    {
        id: uuidv4(),
        question: 'Aký typ projektu by ťa zaujal?',
        options: [
            { id: uuidv4(), text: 'Postaviť alebo zrekonštruovať dom', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Rozbehnúť startup s novým produktom', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Založiť komunitné centrum pre mladých', type: HollandType.SOCIAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Čo by si robil/a na tábore ako vedúci?',
        options: [
            { id: uuidv4(), text: 'Učil/a deti praktické zručnosti', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Organizoval/a súťaže a motivoval/a tímy', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Staral/a sa o pohodu a vzťahy v skupine', type: HollandType.SOCIAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Ktorý aspekt práce by bol pre teba najdôležitejší?',
        options: [
            { id: uuidv4(), text: 'Práca s hmatateľnými vecami a výsledkami', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Možnosť viesť a ovplyvňovať smerovanie', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Pozitívny vplyv na životy ľudí', type: HollandType.SOCIAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Ako by si prispel/a k úspechu tímu?',
        options: [
            { id: uuidv4(), text: 'Praktickými zručnosťami a technickou pomocou', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Vedením, stratégiou a energiou', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Podporou, spoluprácou a empatiou', type: HollandType.SOCIAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Čo by ťa robilo hrdým/hrdou na svoju prácu?',
        options: [
            { id: uuidv4(), text: 'Kvalitne vyrobený produkt alebo opravená vec', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Úspešne uzavretý obchod alebo projekt', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Vďačnosť ľudí, ktorým som pomohol/a', type: HollandType.SOCIAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Akú kariéru by si si vybral/a?',
        options: [
            { id: uuidv4(), text: 'Technik, remeselník alebo operátor', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Obchodník, manažér alebo podnikateľ', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Poradca, pedagóg alebo terapeut', type: HollandType.SOCIAL }
        ]
    },

    // === Triad Set 6: I-A-C (Investigative, Artistic, Conventional) ===
    {
        id: uuidv4(),
        question: 'Ako by si riešil/a neznámy problém?',
        options: [
            { id: uuidv4(), text: 'Systematicky by som preskúmal/a všetky fakty', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Použil/a by som intuíciu a kreatívne myslenie', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Hľadal/a by som osvedčené postupy a pravidlá', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Ktorý typ knihy by si si prečítal/a?',
        options: [
            { id: uuidv4(), text: 'Populárno-vedeckú alebo odbornú', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Umeleckú beletriu alebo poéziu', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Príručku alebo manuál k praktickým zručnostiam', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Ako by si opísal/a svoj prístup k práci?',
        options: [
            { id: uuidv4(), text: 'Analytický a zvedavý', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Kreatívny a spontánny', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Organizovaný a systematický', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Čo by ťa zaujalo na novom projekte?',
        options: [
            { id: uuidv4(), text: 'Možnosť preskúmať niečo neznáme', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Príležitosť vytvoriť niečo jedinečné', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Jasne definované ciele a postupy', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Aké prostredie by ti vyhovovalo?',
        options: [
            { id: uuidv4(), text: 'Tiché, kde môžem premýšľať a analyzovať', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Inšpiratívne, plné podnetov a farieb', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Usporiadané, s jasnými pravidlami', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Aký typ úlohy by si preferoval/a?',
        options: [
            { id: uuidv4(), text: 'Zložitú úlohu vyžadujúcu hlboké pochopenie', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Otvorenú úlohu s priestorom pre kreativitu', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Štruktúrovanú úlohu s jasnými kritériami', type: HollandType.CONVENTIONAL }
        ]
    },

    // === Triad Set 7: R-I-E (Realistic, Investigative, Enterprising) ===
    {
        id: uuidv4(),
        question: 'Ako by si najradšej využil/a svoje schopnosti?',
        options: [
            { id: uuidv4(), text: 'Výrobou a opravou vecí', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Výskumom a vývojom', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Vedením a riadením projektov', type: HollandType.ENTERPRISING }
        ]
    },
    {
        id: uuidv4(),
        question: 'Čo by ťa na startup firme zaujalo?',
        options: [
            { id: uuidv4(), text: 'Vývoj a výroba produktu', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Výskum a inovácie', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Budovanie tímu a biznis stratégia', type: HollandType.ENTERPRISING }
        ]
    },
    {
        id: uuidv4(),
        question: 'Ktorá pozícia v IT firme by ťa lákala?',
        options: [
            { id: uuidv4(), text: 'Hardvérový inžinier alebo technik', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Dátový vedec alebo výskumník', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Produktový manažér alebo CTO', type: HollandType.ENTERPRISING }
        ]
    },
    {
        id: uuidv4(),
        question: 'Aký typ práce by ti vyhovoval?',
        options: [
            { id: uuidv4(), text: 'Práca v teréne alebo v dielni', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Práca s dátami a analýzami', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Práca s ľuďmi a obchodom', type: HollandType.ENTERPRISING }
        ]
    },
    {
        id: uuidv4(),
        question: 'Čím by si chcel/a byť známy/a?',
        options: [
            { id: uuidv4(), text: 'Šikovnosťou a praktickými schopnosťami', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Odbornosťou a znalosťami', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Schopnosťou viesť a dosahovať ciele', type: HollandType.ENTERPRISING }
        ]
    },
    {
        id: uuidv4(),
        question: 'Ako by si opísal/a svoj pracovný štýl?',
        options: [
            { id: uuidv4(), text: 'Praktický a orientovaný na akciu', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Analytický a orientovaný na poznanie', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Strategický a orientovaný na výsledky', type: HollandType.ENTERPRISING }
        ]
    },

    // === Triad Set 8: A-S-C (Artistic, Social, Conventional) ===
    {
        id: uuidv4(),
        question: 'Aký typ práce by ťa napĺňal?',
        options: [
            { id: uuidv4(), text: 'Tvorba obsahu a komunikácia cez médiá', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Priama práca s ľuďmi a ich podpora', type: HollandType.SOCIAL },
            { id: uuidv4(), text: 'Správa systémov a procesov', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Ako by si chcel/a ovplyvniť svet?',
        options: [
            { id: uuidv4(), text: 'Umením a kreatívnym vyjadrením', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Priamou pomocou a vzdelávaním', type: HollandType.SOCIAL },
            { id: uuidv4(), text: 'Efektívnymi systémami a organizáciou', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Aký typ komunikácie preferuješ?',
        options: [
            { id: uuidv4(), text: 'Expresívnu a vizuálnu', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Osobnú a empatickú', type: HollandType.SOCIAL },
            { id: uuidv4(), text: 'Štruktúrovanú a jasnú', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'V akej organizácii by si pracoval/a?',
        options: [
            { id: uuidv4(), text: 'V kreatívnej agentúre alebo médiách', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'V neziskovke alebo škole', type: HollandType.SOCIAL },
            { id: uuidv4(), text: 'V banke alebo štátnej správe', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Čo by bolo tvojou hlavnou silou?',
        options: [
            { id: uuidv4(), text: 'Kreativita a originalita', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Empatia a schopnosť počúvať', type: HollandType.SOCIAL },
            { id: uuidv4(), text: 'Presnosť a spoľahlivosť', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Aký typ detailov ti vadí prehliadať?',
        options: [
            { id: uuidv4(), text: 'Estetické nedostatky a vizuálne chyby', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Keď niekto potrebuje pomoc a nevšimnem si', type: HollandType.SOCIAL },
            { id: uuidv4(), text: 'Formálne chyby a nepresnosti v dokumentoch', type: HollandType.CONVENTIONAL }
        ]
    },

    // === Triad Set 9: R-S-C (Realistic, Social, Conventional) ===
    {
        id: uuidv4(),
        question: 'Akú prácu by si robil/a v nemocnici?',
        options: [
            { id: uuidv4(), text: 'Technik zdravotníckych prístrojov', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Zdravotná sestra alebo terapeut', type: HollandType.SOCIAL },
            { id: uuidv4(), text: 'Zdravotný administrátor', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Ako by si pomohol/a v komunite?',
        options: [
            { id: uuidv4(), text: 'Opravami a údržbou verejných priestorov', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Organizovaním podporných skupín', type: HollandType.SOCIAL },
            { id: uuidv4(), text: 'Správou komunitných financií', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Čo by ti dávalo najväčšiu spokojnosť?',
        options: [
            { id: uuidv4(), text: 'Keď niečo opravím a funguje to', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Keď niekomu pomôžem vyriešiť problém', type: HollandType.SOCIAL },
            { id: uuidv4(), text: 'Keď mám všetko perfektne zorganizované', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Akú pozíciu by si mal/a v škole?',
        options: [
            { id: uuidv4(), text: 'Správca budov a techniky', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Školský psychológ alebo poradca', type: HollandType.SOCIAL },
            { id: uuidv4(), text: 'Vedúci administratívy', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Čo by si robil/a na svadbe ako profesionál?',
        options: [
            { id: uuidv4(), text: 'Staval/a by som stany a techniku', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Bol/a by som koordinátor hostí', type: HollandType.SOCIAL },
            { id: uuidv4(), text: 'Spravoval/a by som rozpočet a platby', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Aký typ práce by si preferoval/a v hoteli?',
        options: [
            { id: uuidv4(), text: 'Údržbár alebo technický pracovník', type: HollandType.REALISTIC },
            { id: uuidv4(), text: 'Concierge starajúci sa o hostí', type: HollandType.SOCIAL },
            { id: uuidv4(), text: 'Recepčný spravujúci rezervácie', type: HollandType.CONVENTIONAL }
        ]
    },

    // === Triad Set 10: I-A-S (Investigative, Artistic, Social) ===
    {
        id: uuidv4(),
        question: 'Ako by si pomohol/a deťom učiť sa?',
        options: [
            { id: uuidv4(), text: 'Vedeckými experimentmi a objavmi', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Kreatívnymi aktivitami a hrami', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Trpezlivým vysvetľovaním a podporou', type: HollandType.SOCIAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Čo by ťa zaujalo v psychológii?',
        options: [
            { id: uuidv4(), text: 'Výskum kognitívnych procesov', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Art terapia a expresívne metódy', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Individuálna terapia a poradenstvo', type: HollandType.SOCIAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Aký typ prezentácie by si pripravil/a?',
        options: [
            { id: uuidv4(), text: 'Podloženú dátami a výskumom', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Vizuálne pútavú a originálnu', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Interaktívnu a zameranú na diskusiu', type: HollandType.SOCIAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Čo by ťa motivovalo pracovať v múzeu?',
        options: [
            { id: uuidv4(), text: 'Výskum a dokumentácia zbierok', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Návrh výstav a expozícií', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Vzdelávacie programy pre návštevníkov', type: HollandType.SOCIAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Aký prístup by si mal/a k riešeniu spoločenských problémov?',
        options: [
            { id: uuidv4(), text: 'Zhromaždiť dáta a analyzovať príčiny', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Vytvoriť kampaň a zmeniť vnímanie', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Priamo pracovať s postihnutými ľuďmi', type: HollandType.SOCIAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Čo by ťa bavilo na univerzite?',
        options: [
            { id: uuidv4(), text: 'Vedecký výskum a publikovanie', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Tvorba vzdelávacích materiálov a dizajn', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Mentorovanie a podpora študentov', type: HollandType.SOCIAL }
        ]
    },

    // === Triad Set 11: I-E-S (Investigative, Enterprising, Social) ===
    {
        id: uuidv4(),
        question: 'Ako by si využil/a svoje znalosti?',
        options: [
            { id: uuidv4(), text: 'Na ďalší výskum a hlbšie pochopenie', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Na budovanie biznisu a produktov', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Na pomoc a vzdelávanie ostatných', type: HollandType.SOCIAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Čo by ťa zaujalo v zdravotníctve?',
        options: [
            { id: uuidv4(), text: 'Lekársky výskum a diagnostika', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Riadenie nemocnice alebo kliniky', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Priama starostlivosť o pacientov', type: HollandType.SOCIAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Aká rola by ti v neziskovke sedela?',
        options: [
            { id: uuidv4(), text: 'Analytik a evaluátor programov', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Riaditeľ a fundraiser', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Terénny pracovník a poradca', type: HollandType.SOCIAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Čo by ťa na novom projekte najviac zaujalo?',
        options: [
            { id: uuidv4(), text: 'Príležitosť naučiť sa niečo nové', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Možnosť viesť tím a rozhodovať', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Šanca pozitívne ovplyvniť ľudí', type: HollandType.SOCIAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Ako by si chcel/a tráviť pracovný čas?',
        options: [
            { id: uuidv4(), text: 'Štúdiom a hľadaním odpovedí', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Stretnutiami a budovaním vzťahov', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Prácou s jednotlivcami a skupinami', type: HollandType.SOCIAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Čo by ťa motivovalo v edtech firme?',
        options: [
            { id: uuidv4(), text: 'Výskum efektívnych metód učenia', type: HollandType.INVESTIGATIVE },
            { id: uuidv4(), text: 'Rast firmy a expanzia na nové trhy', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Priamy dopad na vzdelávanie študentov', type: HollandType.SOCIAL }
        ]
    },

    // === Triad Set 12: A-E-C (Artistic, Enterprising, Conventional) ===
    {
        id: uuidv4(),
        question: 'Akú pozíciu by si mal/a v marketingovej agentúre?',
        options: [
            { id: uuidv4(), text: 'Kreatívny riaditeľ alebo dizajnér', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Account manažér alebo obchodník', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Projektový koordinátor alebo trafficker', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Čo by ťa bavilo na organizácii eventu?',
        options: [
            { id: uuidv4(), text: 'Navrhnúť vizuálnu identitu a atmosféru', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Získavať sponzorov a partnerov', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Plánovať harmonogram a logistiku', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Ako by si prispel/a k úspechu e-shopu?',
        options: [
            { id: uuidv4(), text: 'Tvorbou atraktívneho obsahu a fotografií', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Predajnou stratégiou a marketingom', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Správou objednávok a zákazníckej podpory', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Čo by ťa zaujalo v módnom priemysle?',
        options: [
            { id: uuidv4(), text: 'Navrhovanie a styling', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Riadenie značky a merchandising', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Nákup a správa zásob', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Akú rolu by si mal/a vo vydavateľstve?',
        options: [
            { id: uuidv4(), text: 'Redaktor alebo ilustrátor', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Vydavateľ alebo marketingový manažér', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Korektor alebo produktový koordinátor', type: HollandType.CONVENTIONAL }
        ]
    },
    {
        id: uuidv4(),
        question: 'Čo by ťa bavilo v architektonickej firme?',
        options: [
            { id: uuidv4(), text: 'Navrhovať estetické a inovatívne riešenia', type: HollandType.ARTISTIC },
            { id: uuidv4(), text: 'Viesť projekty a komunikovať s klientmi', type: HollandType.ENTERPRISING },
            { id: uuidv4(), text: 'Spracovávať dokumentáciu a povolenia', type: HollandType.CONVENTIONAL }
        ]
    }
]

// Fisher-Yates shuffle algorithm
function shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array]
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        const left = shuffled[i]
        const right = shuffled[j]

        if (left === undefined || right === undefined) {
            continue
        }

        shuffled[i] = right
        shuffled[j] = left
    }
    return shuffled
}

// Number of questions to use in the survey - use all 72 for maximum reliability
export const HOLLAND_QUESTION_COUNT = 72

// Get random subset of questions with balanced type coverage
function getRandomQuestionSubset(): HollandQuestionType[] {
    const shuffled = shuffleArray(HOLLAND_QUESTIONS)
    return shuffled.slice(0, HOLLAND_QUESTION_COUNT)
}

// Initialize a new Holland survey with random question subset
export function initializedHollandSurvey(userId: UserId): Omit<HollandSurvey, 'id' | 'createdAt'> {
    return {
        surveyType: SurveyTypes.HOLLAND,
        userId,
        hash: uuidv4(),
        questions: getRandomQuestionSubset().map((q) => ({ ...q, value: undefined })),
        scores: {
            [HollandType.REALISTIC]: 0,
            [HollandType.INVESTIGATIVE]: 0,
            [HollandType.ARTISTIC]: 0,
            [HollandType.SOCIAL]: 0,
            [HollandType.ENTERPRISING]: 0,
            [HollandType.CONVENTIONAL]: 0
        },
        dominantType: HollandType.REALISTIC,
        hollandCode: 'RIA',
        confidence: 0,
        isDraft: true,
        deletedAt: null
    }
}
